import {
  Component, Input, signal, OnInit, OnDestroy,
  PLATFORM_ID, inject, ChangeDetectionStrategy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HeroSlide } from '../../../core/config/site-config.interface';

export type { HeroSlide };

@Component({
  selector: 'app-hero-slider',
  standalone: true,
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="absolute inset-0 overflow-hidden">
      <!-- Slide images -->
      @for (slide of slides; track $index; let i = $index) {
        <div class="absolute inset-0 transition-opacity duration-[1200ms] ease-in-out"
             [class.opacity-100]="activeIndex() === i"
             [class.opacity-0]="activeIndex() !== i"
             [class.z-[1]]="activeIndex() === i"
             [class.z-0]="activeIndex() !== i">
          <img [src]="slide.image" [alt]="slide.title"
               class="absolute inset-0 w-full h-full object-cover transition-transform duration-[8000ms] ease-out"
               [class.scale-110]="activeIndex() === i"
               [class.scale-100]="activeIndex() !== i" />
        </div>
      }

      <!-- Overlays -->
      <div class="absolute inset-0 bg-surface-950/55 z-[2]"></div>
      <div class="absolute inset-0 bg-gradient-to-b from-brand-950/40 via-transparent to-surface-950 z-[2]"></div>

      <!-- Slide content -->
      <div class="absolute inset-0 z-[3] flex items-center">
        <div class="mx-auto max-w-7xl px-6 lg:px-8 w-full">
          @for (slide of slides; track $index; let i = $index) {
            <div class="max-w-2xl transition-all duration-700"
                 [class.opacity-100]="activeIndex() === i"
                 [class.opacity-0]="activeIndex() !== i"
                 [class.translate-y-0]="activeIndex() === i"
                 [class.translate-y-4]="activeIndex() !== i"
                 [class.absolute]="activeIndex() !== i"
                 [class.pointer-events-none]="activeIndex() !== i">
              <h2 class="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-[1.1] tracking-tight mb-4">
                {{ slide.title }}
              </h2>
              <p class="text-base md:text-lg text-white/75 leading-relaxed mb-7 max-w-lg">
                {{ slide.description }}
              </p>
              <a [routerLink]="slide.cta.link"
                 class="inline-flex items-center gap-2.5 px-7 py-3 bg-brand-500 text-white font-semibold text-sm rounded-lg transition-all duration-300 hover:bg-brand-600 hover:shadow-lg hover:shadow-brand-500/25">
                {{ slide.cta.text }}
                <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
            </div>
          }
        </div>
      </div>

      <!-- Slide indicators -->
      <div class="absolute bottom-8 right-8 z-20 flex items-center gap-2.5">
        @for (slide of slides; track $index; let i = $index) {
          <button (click)="goTo(i)"
                  [attr.aria-label]="'Go to slide ' + (i + 1)"
                  class="group relative h-1.5 rounded-full transition-all duration-500 overflow-hidden"
                  [class.w-10]="activeIndex() === i"
                  [class.bg-white]="activeIndex() === i"
                  [class.w-5]="activeIndex() !== i"
                  [class.bg-white/30]="activeIndex() !== i">
            @if (activeIndex() === i) {
              <div class="absolute inset-0 bg-brand-400 rounded-full origin-left slider-progress"></div>
            }
          </button>
        }
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; position: absolute; inset: 0; }
    .slider-progress {
      animation: slider-progress 6s linear forwards;
    }
    @keyframes slider-progress {
      from { transform: scaleX(0); }
      to { transform: scaleX(1); }
    }
  `],
})
export class HeroSliderComponent implements OnInit, OnDestroy {
  @Input() slides: HeroSlide[] = [];
  @Input() interval = 6000;

  activeIndex = signal(0);
  private timer: ReturnType<typeof setInterval> | null = null;
  private platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.slides.length > 1) {
      this.startAutoplay();
    }
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
  }

  goTo(index: number): void {
    this.activeIndex.set(index);
    this.restartAutoplay();
  }

  private startAutoplay(): void {
    this.timer = setInterval(() => {
      this.activeIndex.update(i => (i + 1) % this.slides.length);
    }, this.interval);
  }

  private stopAutoplay(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private restartAutoplay(): void {
    this.stopAutoplay();
    this.startAutoplay();
  }
}
